import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCw, ArrowRight, Phone, IdCard, Activity, AlertTriangle, UserX } from "lucide-react";
import api from "@/lib/api";

const KIND_META = {
  duplicate_phone: { icon: Phone, color: "text-orange-700 bg-orange-50 border-orange-200", label: "Numéro réutilisé" },
  duplicate_kyc: { icon: IdCard, color: "text-red-700 bg-red-50 border-red-200", label: "Pièce KYC réutilisée" },
  burst_24h: { icon: Activity, color: "text-amber-700 bg-amber-50 border-amber-200", label: "Activité inhabituelle" },
  anomalous_amount: { icon: AlertTriangle, color: "text-yellow-700 bg-yellow-50 border-yellow-200", label: "Montant anormal" },
  self_referral: { icon: UserX, color: "text-red-700 bg-red-50 border-red-200", label: "Parrainage suspect" },
};

const SEVERITY_BG = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-gray-100 text-gray-700",
};

export default function AdminFraud() {
  const [data, setData] = useState({ alerts: [], computed_at: null });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fraud-alerts");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const grouped = data.alerts.reduce((acc, a) => {
    acc[a.kind] = acc[a.kind] || [];
    acc[a.kind].push(a);
    return acc;
  }, {});

  return (
    <div data-testid="admin-fraud-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-red-50 text-red-700 grid place-items-center"><ShieldAlert size={22} /></span>
          <div>
            <h1 className="font-heading text-3xl font-bold">Détection anti-fraude</h1>
            <p className="text-gray-600 text-sm mt-1">
              Alertes automatiques calculées sur l&apos;ensemble des comptes et transactions.
              {data.computed_at && <> Dernière analyse : {new Date(data.computed_at).toLocaleString("fr-FR")}</>}
            </p>
          </div>
        </div>
        <button onClick={refresh} disabled={loading} className="sl-btn-outline text-sm" data-testid="fraud-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="fraud-summary">
        {Object.entries(KIND_META).map(([kind, meta]) => (
          <div key={kind} className={`rounded-xl border-2 p-3 ${meta.color}`}>
            <div className="flex items-center gap-2">
              <meta.icon size={16} />
              <span className="text-xs font-bold uppercase tracking-widest">{meta.label}</span>
            </div>
            <div className="mt-1 font-heading text-2xl font-bold" data-testid={`fraud-count-${kind}`}>
              {(grouped[kind] || []).length}
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="sl-card mt-6 text-center text-gray-500">Analyse en cours…</div>
      ) : data.alerts.length === 0 ? (
        <div className="sl-card mt-6 text-center text-gray-600 py-10" data-testid="fraud-empty">
          <ShieldAlert size={36} className="text-emerald-500 mx-auto mb-3" />
          <div className="font-semibold">Aucune alerte de fraude détectée</div>
          <div className="text-sm text-gray-500 mt-1">Toutes les règles ont été contrôlées : numéros, pièces d&apos;identité, fréquence, montants et parrainages.</div>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="fraud-alerts-list">
          {data.alerts.map((a, i) => {
            const meta = KIND_META[a.kind] || { icon: ShieldAlert, color: "text-gray-700 bg-gray-50 border-gray-200", label: a.kind };
            return (
              <div key={i} className={`rounded-xl border-2 p-4 ${meta.color}`} data-testid={`fraud-alert-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-white grid place-items-center flex-shrink-0"><meta.icon size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{meta.label}</span>
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${SEVERITY_BG[a.severity] || ""}`}>
                        {a.severity}
                      </span>
                      <span className="text-xs text-gray-500 font-mono truncate">{a.subject}</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{a.detail}</div>
                    {a.users && a.users.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-2">
                        {a.users.map((u, j) => (
                          <span key={j} className="inline-block bg-white border border-gray-200 rounded-full px-2 py-0.5">
                            {u.email || u.phone || u.name || u.id}
                          </span>
                        ))}
                      </div>
                    )}
                    {a.tx_codes && a.tx_codes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {a.tx_codes.slice(0, 8).map((c) => (
                          <Link key={c} to={`/admin/transactions/by-code/${c}`} className="text-[11px] font-mono bg-white border border-gray-200 rounded px-2 py-0.5 hover:bg-gray-50">
                            {c}
                          </Link>
                        ))}
                        {a.tx_codes.length > 8 && (
                          <span className="text-[11px] text-gray-500 self-center">+{a.tx_codes.length - 8}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
